import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';

const POLL_INTERVAL_MS = 60_000;
const STORAGE_KEY = 'mensajes_no_leidos_seen_v1';

/**
 * Hook global que consulta `/api/mensajes/no-leidos` cada 30s y muestra un toast
 * cuando aparecen mensajes nuevos (no vistos en sesiones anteriores).
 *
 * - Persiste los IDs ya notificados en localStorage para no spamear al usuario al recargar.
 * - El toast es clickeable y navega a la OT correspondiente.
 * - Solo activo cuando hay usuario autenticado.
 */
export function useMensajesPoller(user) {
  const navigate = useNavigate();
  const seenRef = useRef(new Set());
  const initializedRef = useRef(false);

  // Cargar IDs ya notificados al montar
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) seenRef.current = new Set(arr);
      }
    } catch {
      seenRef.current = new Set();
    }
  }, []);

  useEffect(() => {
    if (!user?.email) return undefined;

    let cancelled = false;
    const isPublic = typeof window !== 'undefined'
      && (window.location.pathname.startsWith('/consulta')
        || window.location.pathname.startsWith('/companias')
        || window.location.pathname === '/login');
    if (isPublic) return undefined;

    const persistSeen = () => {
      try {
        // Limitar a los últimos 200 ids para no crecer indefinidamente
        const arr = Array.from(seenRef.current).slice(-200);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
      } catch {
        /* ignore quota errors */
      }
    };

    const fetchOnce = async () => {
      // No hacer polling si la pestaña está en background
      if (typeof document !== 'undefined' && document.hidden) return;
      try {
        const res = await api.get('/mensajes/no-leidos');
        if (cancelled) return;
        const items = res?.data?.items || [];

        // Primera ejecución de la sesión: sembrar el set sin disparar toasts
        // (evita spam al iniciar sesión con backlog previo).
        if (!initializedRef.current) {
          items.forEach(it => seenRef.current.add(it.mensaje_id));
          persistSeen();
          initializedRef.current = true;
          return;
        }

        const nuevos = items.filter(it => it.mensaje_id && !seenRef.current.has(it.mensaje_id));
        if (nuevos.length === 0) return;

        // Si hay más de 3, mostrar un toast agregado
        if (nuevos.length > 3) {
          toast.message(`📨 ${nuevos.length} mensajes internos nuevos`, {
            description: 'Pulsa para ver el primero',
            action: {
              label: 'Ver',
              onClick: () => {
                const first = nuevos[0];
                if (first?.orden_id) navigate(`/ordenes/${first.orden_id}`);
              },
            },
            duration: 12_000,
          });
        } else {
          nuevos.forEach(n => {
            const titulo = `💬 ${n.autor_nombre || 'Mensaje nuevo'} · ${n.numero_orden || ''}`.trim();
            toast.message(titulo, {
              description: n.preview || 'Nuevo mensaje',
              action: {
                label: 'Abrir',
                onClick: () => navigate(`/ordenes/${n.orden_id}`),
              },
              duration: 10_000,
            });
          });
        }

        nuevos.forEach(n => seenRef.current.add(n.mensaje_id));
        persistSeen();
      } catch {
        // ignorar errores transitorios de red
      }
    };

    // Lanzar primer fetch tras un pequeño delay para no competir con la carga inicial
    const initTimer = setTimeout(fetchOnce, 4000);
    const interval = setInterval(fetchOnce, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(initTimer);
      clearInterval(interval);
    };
  }, [user?.email, navigate]);
}

export default useMensajesPoller;
